<template>
  <div class="issuance-birdseye">
    <div class="issuance-birdseye__header">
      <span
        class="text-uppercase text-secondary fw-bold fs-10 letter-spacing-eyebrow"
      >
        Issuance status — last 12 months
      </span>
      <div class="issuance-birdseye__legend">
        <span class="issuance-birdseye__legend-item">
          <span
            class="issuance-birdseye__cell issuance-birdseye__cell--green"
          ></span>
          All issued
        </span>
        <span class="issuance-birdseye__legend-item">
          <span
            class="issuance-birdseye__cell issuance-birdseye__cell--orange"
          ></span>
          Partial
        </span>
        <span class="issuance-birdseye__legend-item">
          <span
            class="issuance-birdseye__cell issuance-birdseye__cell--red"
          ></span>
          Late, none issued
        </span>
        <span class="issuance-birdseye__legend-item">
          <span
            class="issuance-birdseye__cell issuance-birdseye__cell--white"
          ></span>
          Pending
        </span>
        <span class="issuance-birdseye__legend-item">
          <span
            class="issuance-birdseye__cell issuance-birdseye__cell--empty"
          ></span>
          No devices
        </span>
      </div>
    </div>
    <div v-if="loading" class="issuance-birdseye__placeholder">Loading...</div>
    <div
      v-else-if="!data || data.rows.length === 0"
      class="issuance-birdseye__placeholder"
    >
      No registry devices to summarise.
    </div>
    <div v-else class="issuance-birdseye__table-wrapper">
      <table class="issuance-birdseye__table">
        <thead>
          <tr>
            <th class="issuance-birdseye__country-col"></th>
            <th
              v-for="(m, idx) in data.months"
              :key="`${m.year}-${m.month}`"
              class="issuance-birdseye__month"
              :class="{
                'issuance-birdseye__month--current':
                  idx === data.months.length - 1,
              }"
            >
              {{ shortMonthLabel(m) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in sortedRows"
            :key="`${row.country}|${row.registry ?? ''}`"
          >
            <th class="issuance-birdseye__country-col">
              <button
                type="button"
                class="issuance-birdseye__country-inner"
                :class="{
                  'issuance-birdseye__country-inner--active':
                    row.country === activeDocCountry,
                }"
                :title="
                  row.country === activeDocCountry
                    ? `Hide ${translateCountryCode(row.country, t, te)} issuance notes`
                    : `Show ${translateCountryCode(row.country, t, te)} issuance notes`
                "
                :aria-expanded="row.country === activeDocCountry"
                @click="$emit('selectCountry', row.country)"
              >
                <span class="issuance-birdseye__flag">{{
                  getFlagEmoji(row.country)
                }}</span>
                <span class="issuance-birdseye__country-name">{{
                  translateCountryCode(row.country, t, te)
                }}</span>
                <span
                  v-if="row.registry"
                  class="issuance-birdseye__registry text-secondary"
                >
                  · {{ registryShortLabel(row.registry) }}
                </span>
                <font-awesome-icon
                  v-if="docCountries?.includes(row.country)"
                  icon="fa-solid fa-file-alt"
                  class="issuance-birdseye__doc-icon text-secondary"
                />
              </button>
            </th>
            <td
              v-for="cell in row.cells"
              :key="`${cell.year}-${cell.month}`"
              class="issuance-birdseye__cell-td"
            >
              <button
                type="button"
                class="issuance-birdseye__cell"
                :class="cellClass(cell.status)"
                :title="cellTitle(row, cell)"
                :aria-label="cellTitle(row, cell)"
                @click="
                  $emit('select', {
                    country: row.country,
                    registry: row.registry,
                    year: cell.year,
                    month: cell.month,
                  })
                "
              ></button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { registryShortLabel } from "@/admin/issuance/issuanceMetrics";
import { MONTH_NAMES } from "@/constants";
import type {
  IssuanceBirdseyeCell,
  IssuanceBirdseyeMonth,
  IssuanceBirdseyeResponse,
  IssuanceBirdseyeRow,
  IssuanceBirdseyeStatus,
} from "@/types";
import { getFlagEmoji, translateCountryCode } from "@/utils/countries";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  data: IssuanceBirdseyeResponse | null;
  loading: boolean;
  activeDocCountry?: string | null;
  docCountries?: string[];
}>();

defineEmits<{
  select: [
    {
      country: string;
      registry: string | null;
      year: number;
      month: number;
    },
  ];
  selectCountry: [country: string];
}>();

const { t, te } = useI18n();

const shortMonthLabel = (m: IssuanceBirdseyeMonth): string =>
  MONTH_NAMES[m.month - 1];

const sortedRows = computed(() => {
  if (!props.data) return [];
  return [...props.data.rows].sort((a, b) => {
    const aName = translateCountryCode(a.country, t, te);
    const bName = translateCountryCode(b.country, t, te);
    const cmp = aName.localeCompare(bName);
    if (cmp !== 0) return cmp;
    return (a.registry ?? "").localeCompare(b.registry ?? "");
  });
});

const cellClass = (status: IssuanceBirdseyeStatus): string =>
  `issuance-birdseye__cell--${status}`;

const formatDeadline = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

const rowLabel = (row: IssuanceBirdseyeRow): string => {
  const country = row.country;
  return row.registry
    ? `${country} · ${registryShortLabel(row.registry)}`
    : country;
};

const cellTitle = (
  row: IssuanceBirdseyeRow,
  cell: IssuanceBirdseyeCell
): string => {
  const month = MONTH_NAMES[cell.month - 1];
  const period = `${month} ${cell.year}`;
  const label = rowLabel(row);
  if (cell.status === "empty" && cell.small_total === 0)
    return `${label} · ${period}: no devices`;
  const small =
    cell.small_total > 0
      ? ` · ${cell.small_issued}/${cell.small_total} small`
      : "";
  const counts = `${cell.devices_issued}/${cell.devices_total} devices issued${small}`;
  const dl = formatDeadline(cell.deadline);
  const deadline = cell.deadline_passed
    ? `deadline ${dl} passed`
    : `before deadline ${dl}`;
  return `${label} · ${period}: ${counts} (${deadline})`;
};
</script>

<style lang="scss" scoped>
.issuance-birdseye {
  background: #fff;
  border: 1px solid var(--phoenix-border-color);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
}

.issuance-birdseye__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.letter-spacing-eyebrow {
  letter-spacing: 0.08em;
}

.issuance-birdseye__legend {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.75rem;
  color: var(--phoenix-gray-700);
}

.issuance-birdseye__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
}

.issuance-birdseye__placeholder {
  padding: 1rem 0;
  color: var(--phoenix-gray-600);
  text-align: center;
  font-size: 0.875rem;
}

.issuance-birdseye__table-wrapper {
  overflow-x: auto;
}

.issuance-birdseye__table {
  border-collapse: collapse;
  font-size: 0.75rem;
  color: var(--phoenix-gray-700);
}

.issuance-birdseye__country-col {
  padding-right: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  text-align: left;
  width: 8rem;
  vertical-align: middle;
}

.issuance-birdseye__country-inner {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font: inherit;
  color: inherit;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
}

.issuance-birdseye__country-inner:hover .issuance-birdseye__country-name {
  text-decoration: underline;
}

.issuance-birdseye__country-inner--active .issuance-birdseye__country-name {
  color: var(--phoenix-primary, #2563eb);
}

.issuance-birdseye__doc-icon {
  font-size: 0.7rem;
}

.issuance-birdseye__registry {
  font-weight: 400;
  font-size: 0.7rem;
}

.issuance-birdseye__table tbody tr:hover {
  background: var(--phoenix-gray-100);
}

.issuance-birdseye__flag {
  font-size: 1rem;
  line-height: 1;
}

.issuance-birdseye__month {
  font-weight: 500;
  text-align: center;
  padding: 0 0.125rem;
  color: var(--phoenix-gray-600);
}

.issuance-birdseye__month--current {
  color: var(--phoenix-gray-1000, #0e1a14);
  font-weight: 700;
}

.issuance-birdseye__cell-td {
  padding: 2px;
  text-align: center;
}

.issuance-birdseye__cell {
  display: inline-block;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 0.25rem;
  border: 1px solid var(--phoenix-border-color);
  vertical-align: middle;
  padding: 0;
  cursor: pointer;
}

.issuance-birdseye__cell:hover {
  outline: 2px solid var(--phoenix-primary, #2563eb);
  outline-offset: 1px;
}

.issuance-birdseye__cell--green {
  background: #4caf50;
  border-color: #3d8b40;
}

.issuance-birdseye__cell--orange {
  background: #ff9800;
  border-color: #cc7a00;
}

.issuance-birdseye__cell--red {
  background: #f44336;
  border-color: #c62828;
}

.issuance-birdseye__cell--white {
  background: #fff;
  border-color: var(--phoenix-gray-300, #cbd5e0);
}

.issuance-birdseye__cell--empty {
  background: var(--phoenix-gray-100);
  border-color: var(--phoenix-gray-200, #edf0f3);
}
</style>
